import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import { AutoScaledImage } from '@atlassian/help-center-common-component/auto-scaled-image';
import { Highlighter } from 'view/help-center/highlighter';
import StarFilledIcon from '@atlaskit/icon/glyph/star-filled';
import StarIcon from '@atlaskit/icon/glyph/star';
import EyeIconUnhide from '@atlaskit/icon/glyph/watch';
import EyeIconHide from '@atlaskit/icon/glyph/watch-filled';
import messages from './messages';
import {
    ProjectName,
    Description,
    Root,
    IconContainer,
    Content,
    Container,
    Actions,
    Wrapper,
    DragHandlerWrapper,
    SortableCardDescription,
} from './styled';

import type { FeaturedProjectCardProps, UnFeaturedProjectCardProps, ProjectCardBasicProps } from './types';
import { token } from '@atlaskit/tokens';
import { colors } from '@atlaskit/theme';

export const ProjectCard: React.FunctionComponent<
    (FeaturedProjectCardProps | UnFeaturedProjectCardProps) & ProjectCardBasicProps & InjectedIntlProps
> = ({
    id,
    name,
    description,
    iconUrl,
    isFeatured,
    isDropped,
    className,
    intl,
    hiddenFromHomeScreen,
    hideActionButtons,
    isPortalHidingEnabled,
    onToggleFeatured,
    onToggleVisibility,
}) => {
    const hasIcon = !!iconUrl;
    const hasOnlyPortalName = !description && !iconUrl;

    const onFeaturedStarClick = React.useCallback(() => {
        onToggleFeatured && onToggleFeatured(id, !isFeatured);
    }, [id, isFeatured, onToggleFeatured]);

    const onVisibilityIconClick = React.useCallback(() => {
        onToggleVisibility && onToggleVisibility(id, !hiddenFromHomeScreen);
    }, [id, hiddenFromHomeScreen, onToggleVisibility]);

    const rootProps = isFeatured
        ? {
              isDropped,
              isFeatured: true as const,
          }
        : {
              isFeatured: false as const,
          };

    const featuredProjectToolTip = intl.formatMessage(messages.featuredProjectTooltip);
    const unFeaturedProjectToolTip = intl.formatMessage(messages.unFeaturedProjectTooltip);
    const visibleProjectTooltip = intl.formatMessage(messages.visibleProjectTooltip);
    const hiddenProjectTooltip = intl.formatMessage(messages.hiddenProjectTooltip);

    const featureToolTip = {
        highlightedMessage: featuredProjectToolTip,
        unHighlightedMessage: unFeaturedProjectToolTip,
    };

    const featureAriaLabel = {
        highlightedLabel: featuredProjectToolTip,
        unHighlightedLabel: unFeaturedProjectToolTip,
    };

    const visibilityToolTip = {
        highlightedMessage: visibleProjectTooltip,
        unHighlightedMessage: hiddenProjectTooltip,
    };

    const visibilityAriaLabel = {
        highlightedLabel: visibleProjectTooltip,
        unHighlightedLabel: hiddenProjectTooltip,
    };

    const ariaAttributes = isFeatured
        ? {
              tabIndex: 0,
              role: 'button',
              'aria-labelledby': `project-card-content-${id}`,
              'aria-roledescription': 'Draggable item',
              'aria-describedby': 'featured-portals-screen-reader-description',
          }
        : {};

    return (
        <Wrapper
            data-test-id={isFeatured ? `featured-card-${id}` : null}
            data-testid={isFeatured ? `featured-card-${id}` : 'unfeatured-card'}
            {...(className ? { className } : {})}
            {...ariaAttributes}
        >
            <Root {...rootProps}>
                <Container hasIcon={hasIcon} isDraggable={isFeatured}>
                    {isFeatured && (
                        <DragHandlerWrapper>
                            <DragHandlerIcon label="drag-handler" size="large" />
                        </DragHandlerWrapper>
                    )}
                    {hasIcon && (
                        <IconContainer>
                            <AutoScaledImage src={iconUrl} alt="icon" />
                        </IconContainer>
                    )}
                    <Content id={`project-card-content-${id}`}>
                        <ProjectName hasOnlyPortalName={hasOnlyPortalName} title={name}>
                            {name}
                        </ProjectName>
                        {!!description && (
                            <Description title={description} dangerouslySetInnerHTML={{ __html: description }} />
                        )}
                    </Content>
                    {!hideActionButtons && (
                        <Actions>
                            {isPortalHidingEnabled && (!isFeatured || hiddenFromHomeScreen) && (
                                <Highlighter
                                    isHighlighted={!hiddenFromHomeScreen}
                                    highlightHandler={onVisibilityIconClick}
                                    toolTip={visibilityToolTip}
                                    ariaLabel={visibilityAriaLabel}
                                    actionSubjectId={hiddenFromHomeScreen ? 'hiddenCard' : 'visibleCard'}
                                    icon={{
                                        // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type 'NamedExoticComponent<IconProps>' is not assi... Remove this comment to see the full error message
                                        HighlightedIcon: EyeIconHide,
                                        // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type 'NamedExoticComponent<IconProps>' is not assi... Remove this comment to see the full error message
                                        UnHighlightedIcon: EyeIconUnhide,
                                    }}
                                />
                            )}

                            {(!isPortalHidingEnabled || !hiddenFromHomeScreen) && (
                                <Highlighter
                                    isHighlighted={isFeatured}
                                    highlightHandler={onFeaturedStarClick}
                                    toolTip={featureToolTip}
                                    ariaLabel={featureAriaLabel}
                                    actionSubjectId={isFeatured ? 'featuredCard' : 'unFeaturedCard'}
                                    icon={{
                                        // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type 'NamedExoticComponent<IconProps>' is not assi... Remove this comment to see the full error message
                                        HighlightedIcon: StarFilledIcon,
                                        // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type 'NamedExoticComponent<IconProps>' is not assi... Remove this comment to see the full error message
                                        UnHighlightedIcon: StarIcon,
                                    }}
                                    color={{
                                        highlightedColor: token('color.background.accent.yellow.subtler', colors.Y200),
                                    }}
                                />
                            )}
                        </Actions>
                    )}
                </Container>
            </Root>
        </Wrapper>
    );
};

export const ScreenReaderInstruction: React.FC<InjectedIntlProps> = ({ intl }) => (
    <SortableCardDescription id="featured-portals-screen-reader-description">
        {intl.formatMessage(messages.screenReaderInstructionsForReordering)}
    </SortableCardDescription>
);

export default React.memo(injectIntl(ProjectCard));
